import { useRef, type PointerEvent, type RefObject } from 'react';

import { wallsApi } from '@walls/services/wallsApi';
import {
  useBoardDragPreviewStore,
  useBoardDrawingStore,
  useBoardSelectionStore,
  useBoardViewportStore,
  useWallContentStore,
  useWallParticipantsStore,
  useWallStore,
  useWorkspaceUiStore,
  type ShapeHandle,
  type WallBoard,
  type WallShape,
} from '@walls/store';
import { useBoardViewportRef } from '../../viewport/BoardViewportContext';
import type { BoardRenderWindow } from '../../viewport/renderWindow';
import {
  getMovableSelectedElements,
  type MovableSelectedCard,
  type MovableSelectedShape,
} from '../../cards/utils/selectedCards';
import { useBoardEdgeAutoScroll } from '../../hooks/useBoardEdgeAutoScroll';
import { updateCardOrClusterPosition } from '../../positioning/persistence';
import type { SortedBoardZIndexes } from '../../positioning/types';
import { sortBoardZIndexes } from '../../positioning/zIndex';
import { hitTestShapes } from '../../shapes/geometry/shapeHitTesting';
import { moveShapeAttributes, resizeShapeAttributes } from '../../shapes/geometry/shapeGeometry';
import { snapLineEndpoint } from '../../shapes/geometry/lineGeometry';
import { getShapeFrontZPos } from '../../shapes/geometry/shapeZIndex';
import { useShapeCommands } from '../../shapes/hooks/useShapeCommands';
import { getShapePermissions } from '../../shapes/hooks/useShapePermissions';

interface UseDrawingPointerEventsOptions {
  board: WallBoard;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  renderOrigin: Pick<BoardRenderWindow, 'x' | 'y'>;
  zoom: number;
}

export function useDrawingPointerEvents({
  board,
  canvasRef,
  renderOrigin,
  zoom,
}: UseDrawingPointerEventsOptions) {
  const clearDraftShape = useBoardDrawingStore((state) => state.clearDraftShape);
  const draftShape = useBoardDrawingStore((state) => state.draftShape);
  const setDraftShape = useBoardDrawingStore((state) => state.setDraftShape);
  const setSelectedShape = useBoardDrawingStore((state) => state.setSelectedShape);
  const setTextInputDraft = useBoardDrawingStore((state) => state.setTextInputDraft);
  const updateDraftShape = useBoardDrawingStore((state) => state.updateDraftShape);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const selectedIds = useBoardSelectionStore((state) => state.selectedIds);
  const setSelectedIds = useBoardSelectionStore((state) => state.setSelectedIds);
  const cards = useWallContentStore((state) => state.cards);
  const shapes = useWallContentStore((state) => state.shapes);
  const updateCard = useWallContentStore((state) => state.updateCard);
  const updateCardZIndexes = useWallContentStore((state) => state.updateCardZIndexes);
  const updateShapeZIndexes = useWallContentStore((state) => state.updateShapeZIndexes);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const topicKey = useWallStore((state) => state.topicKey);
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const visibleParticipantIds = useWorkspaceUiStore((state) => state.visibleParticipantIds);
  const selectedShape = useWorkspaceUiStore((state) => state.selectedShape);
  const selectedPenColor = useWorkspaceUiStore((state) => state.selectedPenColor);
  const selectedPenThickness = useWorkspaceUiStore((state) => state.selectedPenThickness);
  const selectedFillColor = useWorkspaceUiStore((state) => state.selectedFillColor);
  const selectedFillOpacity = useWorkspaceUiStore((state) => state.selectedFillOpacity);
  const selectedLineColor = useWorkspaceUiStore((state) => state.selectedLineColor);
  const selectedLineThickness = useWorkspaceUiStore((state) => state.selectedLineThickness);
  const setSelectedTool = useWorkspaceUiStore((state) => state.setSelectedTool);
  const setCurrentMousePosition = useBoardViewportStore((state) => state.setCurrentMousePosition);
  const setDropPosition = useBoardViewportStore((state) => state.setDropPosition);
  const clearCardDragPreviews = useBoardDragPreviewStore(
    (state) => state.clearCardDragPreviews
  );
  const setCardDragPreviews = useBoardDragPreviewStore((state) => state.setCardDragPreviews);
  const { createShape, persistShape, updateShapeAttributes, updateShapeZPos } =
    useShapeCommands(board);
  const viewportRef = useBoardViewportRef();
  const edgeAutoScroll = useBoardEdgeAutoScroll(viewportRef ?? { current: null });
  const shapeDragRef = useRef<{
    draggedCards: MovableSelectedCard[];
    draggedShapes: MovableSelectedShape[];
    handle: ShapeHandle;
    isPromoted: boolean;
    lastPosition: { x: number; y: number };
    latestShape: WallShape;
    promotedZIndexes: SortedBoardZIndexes | null;
    shapeId: string;
    startPosition: { x: number; y: number };
  } | null>(null);

  const drawingEnabled = Boolean(
    board.allowDrawingTools &&
      (board.isOwner || (currentParticipantId && (board.isMember || board.canCollaborate)))
  );
  const canHandleTool = drawingEnabled && ['shape', 'line', 'text'].includes(selectedTool);
  const canHandlePointer = drawingEnabled && selectedTool === 'pointer';

  return {
    drawingEnabled: canHandleTool || canHandlePointer,
    onPointerDown: (event: PointerEvent<HTMLCanvasElement>) => {
      if (!canHandleTool && !canHandlePointer) {
        return;
      }

      edgeAutoScroll.stop();
      const position = getCanvasPoint(event, canvasRef.current, renderOrigin, zoom);
      setCurrentMousePosition(position);
      setDropPosition(position);

      if (canHandlePointer) {
        clearCardDragPreviews();
        const context = canvasRef.current?.getContext('2d');
        const hit = hitTestShapes(shapes, position, context, zoom);

        if (!hit) {
          setSelectedShape(null);
          setCurrentElement(null);
          return;
        }

        const hitPermissions = getShapePermissions(board, hit.shape, currentParticipantId);

        if (!hitPermissions.canSelect) {
          setSelectedShape(null);
          setCurrentElement(null);
          setSelectedIds([]);
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        setSelectedShape(hit.shape.id, hit.handle);
        setCurrentElement({
          id: hit.shape.id,
          type: hit.shape.type === 'line' ? 'line' : hit.shape.type === 'text' ? 'text' : 'shape',
        });

        const shouldMoveSelection = !hit.handle && selectedIds.includes(hit.shape.id);
        const draggedElements = hit.handle
          ? hitPermissions.canResize
            ? {
                cards: [],
                shapes: [{ shape: hit.shape, startAttributes: { ...hit.shape.attributes } }],
              }
            : { cards: [], shapes: [] }
          : getMovableSelectedElements({
              board,
              cards,
              currentParticipantId,
              pressedElementId: hit.shape.id,
              selectedIds,
              shapes,
              visibleParticipantIds,
            });

        if (!shouldMoveSelection) {
          setSelectedIds([]);
        }

        if (hit.shape.isLocked || (hit.handle && !hitPermissions.canResize)) {
          return;
        }

        if (draggedElements.cards.length === 0 && draggedElements.shapes.length === 0) {
          return;
        }

        event.currentTarget.setPointerCapture(event.pointerId);
        shapeDragRef.current = {
          draggedCards: draggedElements.cards,
          draggedShapes: draggedElements.shapes,
          handle: hit.handle,
          isPromoted: false,
          lastPosition: position,
          latestShape: hit.shape,
          promotedZIndexes: null,
          shapeId: hit.shape.id,
          startPosition: position,
        };
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (selectedTool === 'text') {
        setTextInputDraft({ ...position, text: '' });
        return;
      }

      setDraftShape(
        createDraftShape({
          board,
          currentParticipantId,
          fillColor: selectedFillColor,
          fillOpacity: selectedFillOpacity,
          lineColor: selectedTool === 'line' ? selectedLineColor : selectedPenColor,
          lineThickness: selectedTool === 'line' ? selectedLineThickness : selectedPenThickness,
          position,
          selectedShape,
          selectedTool,
        })
      );
    },
    onPointerMove: (event: PointerEvent<HTMLCanvasElement>) => {
      if (shapeDragRef.current && canHandlePointer) {
        event.preventDefault();
        event.stopPropagation();
        edgeAutoScroll.update({ clientX: event.clientX, clientY: event.clientY });

        const position = getCanvasPoint(event, canvasRef.current, renderOrigin, zoom);
        const draggingShape =
          shapes.find((shape) => shape.id === shapeDragRef.current?.shapeId) ??
          shapeDragRef.current.latestShape;

        const draggingShapePermissions = getShapePermissions(
          board,
          draggingShape,
          currentParticipantId
        );

        if (
          !draggingShape ||
          draggingShape.isLocked ||
          (shapeDragRef.current.handle
            ? !draggingShapePermissions.canResize
            : !draggingShapePermissions.canMove)
        ) {
          return;
        }

        if (!shapeDragRef.current.isPromoted) {
          if (shapeDragRef.current.handle) {
            const frontZPos = getShapeFrontZPos(shapes, draggingShape.id);

            if (frontZPos != null && frontZPos !== (draggingShape.zPos ?? 0)) {
              updateShapeZPos(draggingShape.id, frontZPos);
              shapeDragRef.current.latestShape = {
                ...shapeDragRef.current.latestShape,
                zPos: frontZPos,
              };
            }
          } else {
            shapeDragRef.current.promotedZIndexes = sortBoardZIndexes(
              cards,
              shapes,
              [
                ...shapeDragRef.current.draggedCards.map((draggedCard) => draggedCard.card.id),
                ...shapeDragRef.current.draggedShapes.map(
                  (draggedShape) => draggedShape.shape.id
                ),
              ]
            );
            updateCardZIndexes(shapeDragRef.current.promotedZIndexes.cards);
            updateShapeZIndexes(shapeDragRef.current.promotedZIndexes.shapes);
          }

          shapeDragRef.current.isPromoted = true;
        }

        const deltaX = position.x - shapeDragRef.current.lastPosition.x;
        const deltaY = position.y - shapeDragRef.current.lastPosition.y;

        if (shapeDragRef.current.handle) {
          const currentZPos = shapeDragRef.current.latestShape.zPos;
          const attributesPatch = resizeShapeAttributes(
            draggingShape,
            shapeDragRef.current.handle,
            deltaX,
            deltaY
          );

          updateShapeAttributes(draggingShape.id, {
            ...draggingShape.attributes,
            ...attributesPatch,
          });
          shapeDragRef.current.latestShape = {
            ...draggingShape,
            zPos: currentZPos,
            attributes: {
              ...draggingShape.attributes,
              ...attributesPatch,
            },
          };
        } else {
          const totalDeltaX = position.x - shapeDragRef.current.startPosition.x;
          const totalDeltaY = position.y - shapeDragRef.current.startPosition.y;

          setCardDragPreviews(
            getCardDragPreviews(shapeDragRef.current.draggedCards, totalDeltaX, totalDeltaY)
          );
          getShapeAttributeUpdates(
            shapeDragRef.current.draggedShapes,
            totalDeltaX,
            totalDeltaY
          ).forEach(({ attributes, shapeId }) => updateShapeAttributes(shapeId, attributes));
          shapeDragRef.current.latestShape =
            shapeDragRef.current.draggedShapes.find(
              (draggedShape) => draggedShape.shape.id === draggingShape.id
            )?.shape ?? draggingShape;
        }

        shapeDragRef.current.lastPosition = position;
        setCurrentMousePosition(position);
        return;
      }

      if (!draftShape || !canHandleTool) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      edgeAutoScroll.update({ clientX: event.clientX, clientY: event.clientY });

      const position = getCanvasPoint(event, canvasRef.current, renderOrigin, zoom);
      setCurrentMousePosition(position);

      if (draftShape.type === 'line') {
        updateDraftShape({ endX: position.x, endY: position.y });
      } else {
        const startX = Number(draftShape.attributes.x);
        const startY = Number(draftShape.attributes.y);

        updateDraftShape({
          height: Math.abs(position.y - startY),
          width: Math.abs(position.x - startX),
          x: Math.min(position.x, startX),
          y: Math.min(position.y, startY),
        });
      }
    },
    onPointerUp: (event: PointerEvent<HTMLCanvasElement>) => {
      if (shapeDragRef.current && canHandlePointer) {
        event.preventDefault();
        event.stopPropagation();
        edgeAutoScroll.stop();

        const currentDrag = shapeDragRef.current;
        const draggedHandle = shapeDragRef.current.handle;
        const draggedShape = shapeDragRef.current.latestShape;
        shapeDragRef.current = null;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }

        if (draggedHandle && draggedShape) {
          const shapeToPersist =
            draggedShape.type === 'line' && (draggedHandle === 'start' || draggedHandle === 'end')
              ? snapLineEndpoint(draggedShape, draggedHandle)
              : draggedShape;

          if (shapeToPersist !== draggedShape) {
            updateShapeAttributes(shapeToPersist.id, shapeToPersist.attributes);
          }

          void persistShape(shapeToPersist);
        }

        if (!draggedHandle) {
          const position = getCanvasPoint(event, canvasRef.current, renderOrigin, zoom);
          const totalDeltaX = position.x - currentDrag.startPosition.x;
          const totalDeltaY = position.y - currentDrag.startPosition.y;
          const shapeUpdates = getShapeAttributeUpdates(
            currentDrag.draggedShapes,
            totalDeltaX,
            totalDeltaY,
            currentDrag.promotedZIndexes
          );

          currentDrag.draggedCards.forEach((draggedCard) => {
            const xPos = Math.round(draggedCard.startX + totalDeltaX);
            const yPos = Math.round(draggedCard.startY + totalDeltaY);

            updateCard(draggedCard.card.id, { xPos, yPos });
            void updateCardOrClusterPosition(
              board.id,
              cards,
              draggedCard.card.id,
              xPos,
              yPos,
              topicKey
            );
          });
          shapeUpdates.forEach(({ attributes, shape, zPos }) => {
            const updatedShape = { ...shape, attributes, zPos };

            updateShapeAttributes(shape.id, attributes);
            void persistShape(updatedShape);
          });

          if (currentDrag.promotedZIndexes) {
            void publishZIndexUpdates(board.id, currentDrag.promotedZIndexes, topicKey);
          }

          clearCardDragPreviews();
        }

        return;
      }

      if (!draftShape || !canHandleTool) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      edgeAutoScroll.stop();

      if (isDrawableShape(draftShape)) {
        void createShape(draftShape);
      }

      clearDraftShape();
      setSelectedTool('pointer');
    },
    onPointerCancel: () => {
      shapeDragRef.current = null;
      clearCardDragPreviews();
      clearDraftShape();
      edgeAutoScroll.stop();
    },
  };
}

interface CreateDraftShapeOptions {
  board: WallBoard;
  currentParticipantId: string | null;
  fillColor: string;
  fillOpacity: number;
  lineColor: string;
  lineThickness: number;
  position: { x: number; y: number };
  selectedShape: string;
  selectedTool: string;
}

function createDraftShape({
  board,
  currentParticipantId,
  fillColor,
  fillOpacity,
  lineColor,
  lineThickness,
  position,
  selectedShape,
  selectedTool,
}: CreateDraftShapeOptions): WallShape {
  const isLine = selectedTool === 'line';
  const type = isLine ? 'line' : selectedShape;

  return {
    id: `local-${board.id}-${Date.now()}`,
    board: board.id,
    ownerId: currentParticipantId ?? undefined,
    type,
    isLocked: false,
    zPos: 999,
    attributes: isLine
      ? {
          endX: position.x,
          endY: position.y,
          lineColor,
          lineOpacity: 100,
          lineThickness,
          startX: position.x,
          startY: position.y,
        }
      : {
          fillColor,
          fillOpacity,
          height: 0,
          lineColor,
          lineOpacity: 100,
          lineThickness,
          width: 0,
          x: position.x,
          y: position.y,
        },
  };
}

function getCanvasPoint(
  event: PointerEvent<HTMLCanvasElement>,
  canvas: HTMLCanvasElement | null,
  renderOrigin: Pick<BoardRenderWindow, 'x' | 'y'>,
  zoom: number
) {
  if (!canvas) {
    return { x: 0, y: 0 };
  }

  const rect = canvas.getBoundingClientRect();

  return {
    x: renderOrigin.x + (event.clientX - rect.left) / zoom,
    y: renderOrigin.y + (event.clientY - rect.top) / zoom,
  };
}

async function publishZIndexUpdates(
  boardId: string,
  zIndexes: SortedBoardZIndexes,
  topicKey: string | null
) {
  await Promise.all([
    zIndexes.cards.length > 0
      ? wallsApi.updateCardZIndexes(boardId, zIndexes.cards, topicKey)
      : Promise.resolve(),
    zIndexes.shapes.length > 0
      ? wallsApi.updateShapeZIndexes(boardId, zIndexes.shapes, topicKey)
      : Promise.resolve(),
  ]);
}

function getShapeAttributeUpdates(
  draggedShapes: MovableSelectedShape[],
  deltaX: number,
  deltaY: number,
  promotedZIndexes?: SortedBoardZIndexes | null
) {
  return draggedShapes.map(({ shape, startAttributes }) => {
    const startShape: WallShape = {
      ...shape,
      attributes: startAttributes,
    };
    const attributesPatch = moveShapeAttributes(startShape, deltaX, deltaY);

    return {
      attributes: {
        ...startAttributes,
        ...attributesPatch,
      },
      shape,
      shapeId: shape.id,
      zPos:
        promotedZIndexes?.shapes.find((zIndex) => zIndex.shapeId === shape.id)?.zPos ??
        shape.zPos ??
        1,
    };
  });
}

function getCardDragPreviews(
  draggedCards: MovableSelectedCard[],
  deltaX: number,
  deltaY: number
) {
  return draggedCards.reduce<Record<string, { deltaX: number; deltaY: number }>>(
    (previews, draggedCard) => {
      previews[draggedCard.card.id] = {
        deltaX: Math.round(draggedCard.startX + deltaX) - draggedCard.startX,
        deltaY: Math.round(draggedCard.startY + deltaY) - draggedCard.startY,
      };

      return previews;
    },
    {}
  );
}

function isDrawableShape(shape: WallShape) {
  if (shape.type === 'line') {
    return (
      shape.attributes.startX !== shape.attributes.endX ||
      shape.attributes.startY !== shape.attributes.endY
    );
  }

  return Number(shape.attributes.width) > 0 || Number(shape.attributes.height) > 0;
}
